import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ApiError, type Scenario, type ScenarioRun as Run, type ScenarioStep as Step, api, unwrap } from '@/api';
import { Badge, Button, Card, Dialog, Empty, Field, Input, PageHeader, Spinner, Textarea, cx } from '@/components/ui';
import { timeAgo } from '@/lib/format';

const keys = { scenarios: ['scenarios'] as const, runs: ['runs'] as const };

const TEMPLATE: { name: string; description: string; durationS: number; steps: Step[] } = {
	name: 'Roadside outage',
	description: 'The roadside PM station goes silent, then the main meter drops off the network for a while.',
	durationS: 60,
	steps: [
		{ atS: 5, instance: 'roadside-pm', action: 'trigger_fault', args: { kind: 'offline', durationS: 15 } },
		{ atS: 20, instance: 'main-meter', action: 'trigger_fault', args: { kind: 'offline', durationS: 15 } },
	],
};

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'> = {
	pending: 'neutral',
	running: 'accent',
	ok: 'ok',
	done: 'ok',
	skipped: 'warn',
	cancelled: 'warn',
	interrupted: 'warn',
	failed: 'danger',
};

const ACTION_LABEL: Record<Step['action'], string> = {
	trigger_fault: 'fault',
	set_params_patch: 'patch',
	start: 'start',
	stop: 'stop',
};

function stepSummary(s: Step): string {
	const args = s.args && typeof s.args === 'object' && Object.keys(s.args).length ? ` ${JSON.stringify(s.args)}` : '';
	return `${ACTION_LABEL[s.action]} ${s.instance}${args}`;
}

function Editor({ scenario, onClose }: { scenario?: Scenario; onClose: () => void }) {
	const initial = scenario ?? TEMPLATE;
	const [name, setName] = useState(initial.name);
	const [description, setDescription] = useState(initial.description);
	const [duration, setDuration] = useState(initial.durationS == null ? '' : String(initial.durationS));
	const [steps, setSteps] = useState(JSON.stringify(initial.steps, null, 2));
	const [errors, setErrors] = useState<Record<string, string>>({});
	const qc = useQueryClient();

	const save = async () => {
		setErrors({});
		let parsed: Step[];
		try {
			parsed = JSON.parse(steps);
		} catch (e) {
			setErrors({ '/steps': `Not JSON: ${(e as Error).message}` });
			return;
		}
		const body = { name, description, durationS: duration.trim() === '' ? null : Number(duration), steps: parsed };
		try {
			if (scenario) {
				await unwrap(api.PUT('/api/v1/scenarios/{id}', { params: { path: { id: scenario.id }, header: { 'If-Match': String(scenario.revision) } }, body }));
			} else {
				await unwrap(api.POST('/api/v1/scenarios', { body }));
			}
			toast.success('Scenario saved');
			qc.invalidateQueries({ queryKey: keys.scenarios });
			onClose();
		} catch (e) {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error((e as Error).message);
		}
	};
	const stepErrors = Object.entries(errors).filter(([k]) => k.startsWith('/steps'));
	return (
		<Dialog open wide onClose={onClose} title={scenario ? `Edit ${scenario.name}` : 'New scenario'}>
			<form
				className="flex flex-col gap-4"
				onSubmit={(e) => {
					e.preventDefault();
					save();
				}}
			>
				<div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
					<Field label="Name" error={errors['/name']}>
						<Input value={name} onChange={(e) => setName(e.target.value)} />
					</Field>
					<Field label="Duration (s)" error={errors['/durationS']} hint="Empty: until the last step">
						<Input type="number" min={0} step="any" value={duration} onChange={(e) => setDuration(e.target.value)} />
					</Field>
				</div>
				<Field label="Description">
					<Input value={description} onChange={(e) => setDescription(e.target.value)} />
				</Field>
				<Field
					label="Steps (JSON)"
					hint={
						<>
							Each step: <code>{'{atS, instance, action, args}'}</code>. <code>instance</code> is a slug or <code>tag:&lt;tag&gt;</code>. Actions: <code>trigger_fault</code>{' '}
							<code>{'{kind, durationS}'}</code>, <code>set_params_patch</code> (a JSON merge patch of the params, in memory only), <code>start</code>, <code>stop</code>. When the run ends,
							everything it changed is put back.
						</>
					}
				>
					<Textarea rows={14} value={steps} onChange={(e) => setSteps(e.target.value)} spellCheck={false} />
				</Field>
				{stepErrors.length > 0 && (
					<ul className="flex flex-col gap-1 text-xs text-danger">
						{stepErrors.map(([path, msg]) => (
							<li key={path}>
								<code>{path}</code>: {msg}
							</li>
						))}
					</ul>
				)}
				<div className="flex justify-end gap-2">
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="primary" type="submit">
						Save
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

/** Steps on a time axis, with a cursor while the run is in progress. */
function Timeline({ run }: { run: Run }) {
	const [now, setNow] = useState(Date.now());
	const running = run.status === 'running';
	useEffect(() => {
		if (!running) return;
		const t = setInterval(() => setNow(Date.now()), 250);
		return () => clearInterval(t);
	}, [running]);
	const span = Math.max(run.durationS, ...run.steps.map((s) => s.atS), 1);
	const elapsed = ((running ? now : Date.parse(run.finishedAt ?? run.startedAt)) - Date.parse(run.startedAt)) / 1000;
	const pct = (s: number) => `${Math.min(100, Math.max(0, (100 * s) / span))}%`;
	return (
		<div className="flex flex-col gap-4">
			<div className="relative h-10 rounded-md bg-muted">
				<div className={cx('absolute inset-y-0 left-0 rounded-md', running ? 'bg-accent/15' : 'bg-fg-muted/10')} style={{ width: pct(elapsed) }} />
				{run.steps.map((s) => (
					<div
						key={s.index}
						className={cx(
							'absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface',
							s.status === 'ok' ? 'bg-ok' : s.status === 'pending' ? 'bg-fg-muted/40' : s.status === 'failed' ? 'bg-danger' : 'bg-warn',
						)}
						style={{ left: pct(s.atS) }}
						title={`+${s.atS}s ${s.action} ${s.instance}: ${s.status}`}
					/>
				))}
				{running && <div className="absolute inset-y-0 w-0.5 bg-accent" style={{ left: pct(elapsed) }} />}
				<span className="absolute right-2 bottom-0.5 text-[10px] text-fg-muted">{span}s</span>
			</div>
			<table className="w-full text-xs">
				<thead className="text-left text-fg-muted">
					<tr>
						<th className="py-1 pr-3 font-medium">At</th>
						<th className="py-1 pr-3 font-medium">Step</th>
						<th className="py-1 pr-3 font-medium">Status</th>
						<th className="py-1 pr-3 font-medium">Ran at</th>
						<th className="py-1 font-medium">Notes</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-border">
					{run.steps.map((s) => (
						<tr key={s.index} className="align-top">
							<td className="py-1.5 pr-3 font-mono">+{s.atS}s</td>
							<td className="py-1.5 pr-3 font-mono">
								{ACTION_LABEL[s.action]} {s.instance}
								{s.targets.length > 0 && s.targets.join(', ') !== s.instance && <span className="text-fg-muted"> → {s.targets.join(', ')}</span>}
							</td>
							<td className="py-1.5 pr-3">
								<Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge>
							</td>
							<td className="py-1.5 pr-3 font-mono whitespace-nowrap">
								{s.offsetMs != null ? `+${(s.offsetMs / 1000).toFixed(2)}s` : '—'}
							</td>
							<td className="py-1.5 text-fg-muted">{s.message}</td>
						</tr>
					))}
				</tbody>
			</table>
			{run.reverted.length > 0 && (
				<div className="text-xs text-fg-muted">
					<span className="font-medium text-fg">Put back:</span> {run.reverted.join(' · ')}
				</div>
			)}
		</div>
	);
}

export function Scenarios() {
	const qc = useQueryClient();
	const scenarios = useQuery({ queryKey: keys.scenarios, queryFn: () => unwrap(api.GET('/api/v1/scenarios')).then((d) => d.items), refetchInterval: 3_000 });
	const runs = useQuery({
		queryKey: keys.runs,
		queryFn: () => unwrap(api.GET('/api/v1/runs', { params: { query: {} } })).then((d) => d.items),
		refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'running') ? 1_000 : 5_000),
	});
	const [editing, setEditing] = useState<{ scenario?: Scenario }>();
	const [selected, setSelected] = useState<string>();
	const refresh = () => {
		qc.invalidateQueries({ queryKey: keys.scenarios });
		qc.invalidateQueries({ queryKey: keys.runs });
	};
	const act = async (f: () => Promise<unknown>, ok: string) => {
		try {
			await f();
			toast.success(ok);
		} catch (e) {
			toast.error((e as Error).message);
		}
		refresh();
	};
	const run = (s: Scenario) =>
		act(async () => {
			const r = await unwrap(api.POST('/api/v1/scenarios/{id}/run', { params: { path: { id: s.id } } }));
			setSelected(r.id);
		}, `${s.name} started`);
	const cancel = (runId: string) => act(() => unwrap(api.DELETE('/api/v1/runs/{id}', { params: { path: { id: runId } } })), 'Run cancelled; changes put back');
	const remove = (s: Scenario) => {
		if (!confirm(`Delete ${s.name}? Its past runs are kept.`)) return;
		act(() => unwrap(api.DELETE('/api/v1/scenarios/{id}', { params: { path: { id: s.id } } })), 'Scenario deleted');
	};
	const shown = runs.data?.find((r) => r.id === selected) ?? runs.data?.[0];

	return (
		<>
			<PageHeader
				title="Scenarios"
				subtitle="Timed faults, parameter changes and start/stop, replayed on demand"
				actions={
					<Button variant="primary" onClick={() => setEditing({})}>
						<Plus className="size-4" /> New scenario
					</Button>
				}
			/>
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="Scenarios">
					{!scenarios.data ? (
						<Spinner />
					) : !scenarios.data.length ? (
						<Empty title="No scenarios yet">Create one to replay an incident: a vibration ramp, a sensor going offline, a line stopping.</Empty>
					) : (
						<ul className="divide-y divide-border">
							{scenarios.data.map((s) => (
								<li key={s.id} className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0">
									<div className="flex items-center gap-2">
										<span className="font-medium">{s.name}</span>
										{s.activeRun && <Badge tone="accent">running</Badge>}
										<span className="ml-auto flex gap-1">
											{s.activeRun ? (
												<Button size="sm" onClick={() => cancel(s.activeRun!)}>
													<Square className="size-3.5" /> Cancel
												</Button>
											) : (
												<Button size="sm" variant="primary" onClick={() => run(s)}>
													<Play className="size-3.5" /> Run
												</Button>
											)}
											<Button size="sm" variant="ghost" aria-label="Edit" onClick={() => setEditing({ scenario: s })}>
												<Pencil className="size-3.5" />
											</Button>
											<Button size="sm" variant="ghost" aria-label="Delete" disabled={!!s.activeRun} onClick={() => remove(s)}>
												<Trash2 className="size-3.5" />
											</Button>
										</span>
									</div>
									{s.description && <p className="text-sm text-fg-muted">{s.description}</p>}
									<ol className="flex flex-col gap-0.5 font-mono text-xs text-fg-muted">
										{s.steps.map((st, i) => (
											<li key={i} className="truncate">
												+{st.atS}s {stepSummary(st)}
											</li>
										))}
									</ol>
									<span className="text-xs text-fg-muted">
										{s.durationS != null ? `lasts ${s.durationS}s` : 'ends after its last step'} · {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
									</span>
								</li>
							))}
						</ul>
					)}
				</Card>
				<Card title="Runs">
					{!runs.data ? (
						<Spinner />
					) : !runs.data.length ? (
						<p className="text-sm text-fg-muted">No runs yet.</p>
					) : (
						<ul className="divide-y divide-border text-sm">
							{runs.data.map((r) => (
								<li key={r.id}>
									<button
										type="button"
										className={cx('flex w-full items-center gap-2 px-2 py-2 text-left hover:bg-muted', shown?.id === r.id && 'bg-muted')}
										onClick={() => setSelected(r.id)}
									>
										<Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
										<span className="truncate">{r.scenarioName}</span>
										<span className="ml-auto text-xs whitespace-nowrap text-fg-muted">{timeAgo(r.startedAt)}</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</Card>
				{shown && (
					<Card
						className="lg:col-span-2"
						title={
							<span className="flex items-center gap-2">
								{shown.scenarioName} <Badge tone={STATUS_TONE[shown.status]}>{shown.status}</Badge>
							</span>
						}
						actions={
							shown.status === 'running' && (
								<Button size="sm" onClick={() => cancel(shown.id)}>
									<Square className="size-3.5" /> Cancel
								</Button>
							)
						}
					>
						<Timeline run={shown} />
					</Card>
				)}
			</div>
			{editing && <Editor scenario={editing.scenario} onClose={() => setEditing(undefined)} />}
		</>
	);
}
